/**
 * 認証フローの手続き（`/api/v1/auth/ops`）。
 *
 * ops は TOTP 必須なので、ログインは常に 2 段階になりうる:
 *   POST /login        → `{mfaRequired:true, mfaTicket}`（2FA 登録済み）
 *   POST /login/mfa    → `{accessToken, user}`
 * チケットは 5 分・5 回まで（02-control-api §3.3）。
 */
import {
  api,
  request,
  setAccessToken,
  refreshAccessToken,
  AUTH_PREFIX,
  PUBLIC_PREFIX,
  ApiError,
} from './api.js';
import { authStore } from '../stores/auth.js';
import type {
  ImpersonateResult,
  ImpersonationSession,
  LoginResponse,
  LoginSuccess,
  OpsUser,
  PublicConfig,
} from '../types/api.js';

export type { ImpersonateResult, ImpersonationSession };

export function isMfaRequired(res: LoginResponse): res is Extract<LoginResponse, { mfaRequired: true }> {
  return 'mfaRequired' in res && res.mfaRequired === true;
}

export async function fetchPublicConfig(): Promise<PublicConfig> {
  return request<PublicConfig>(`${PUBLIC_PREFIX}/config`, {
    method: 'GET',
    anonymous: true,
    noRefresh: true,
    silent: true,
  });
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const res = await request<LoginResponse>(`${AUTH_PREFIX}/login`, {
    method: 'POST',
    body: { email, password },
    anonymous: true,
    noRefresh: true,
    silent: true, // ログイン画面はフォーム内 Alert で見せる
  });
  if (!isMfaRequired(res)) await adoptSession(res);
  return res;
}

export async function verifyMfa(
  mfaTicket: string,
  method: 'totp' | 'recovery',
  code: string,
): Promise<LoginSuccess> {
  const res = await request<LoginSuccess>(`${AUTH_PREFIX}/login/mfa`, {
    method: 'POST',
    body: { mfaTicket, method, code },
    anonymous: true,
    noRefresh: true,
    silent: true,
  });
  await adoptSession(res);
  return res;
}

/**
 * dev 限定のワンクリックログイン。
 * 本番ではルート自体が登録されないので 404 になる（§7.11 層1/層3）。
 */
export async function devLogin(): Promise<LoginSuccess> {
  const res = await request<LoginSuccess>(`${AUTH_PREFIX}/dev-login`, {
    method: 'POST',
    body: { as: 'ops' },
    anonymous: true,
    noRefresh: true,
    silent: true,
  });
  await adoptSession(res);
  return res;
}

export async function logout(): Promise<void> {
  try {
    await request<void>(`${AUTH_PREFIX}/logout`, { method: 'POST', noRefresh: true, silent: true });
  } catch {
    // サーバー側の失効に失敗しても、ローカルのセッションは必ず捨てる。
  }
  setAccessToken(null);
  authStore.clear();
}

export async function fetchMe(): Promise<OpsUser> {
  return request<OpsUser>(`${AUTH_PREFIX}/me`, { method: 'GET', silent: true });
}

/**
 * ログイン応答からセッションを確立する。
 *
 * `/login` / `/login/mfa` の `user` には `permissions` が入っていない（API 実装がそう）。
 * ストアに入れてしまうと `user.permissions` が undefined になり、ナビの権限フィルタや
 * ダッシュボードの `enabled` 判定が実行時に落ちる。トークンを載せた直後に `/me` を
 * 引き直して、ストアには**常に完全な `OpsUser`** だけを入れる。
 */
async function adoptSession(res: LoginSuccess): Promise<void> {
  setAccessToken(res.accessToken);
  authStore.setUser(await fetchMe());
}

/**
 * 起動時のセッション復帰。
 * アクセストークンはメモリにしか無いので、まず refresh Cookie で取り直してから `/me`。
 * 失敗しても throw せず anonymous にするだけ（ログイン画面は常に描けること）。
 */
export async function bootstrapSession(): Promise<void> {
  const token = await refreshAccessToken();
  if (!token) {
    authStore.clear();
    return;
  }
  try {
    authStore.setUser(await fetchMe());
  } catch (err) {
    // 401 以外（500 等）でもログイン画面に落とす。中途半端に認証済みにしない。
    if (!(err instanceof ApiError)) throw err;
    setAccessToken(null);
    authStore.clear();
  }
}

/* --------------------------------------------------------------- 2 要素認証 */

export interface TotpSetup {
  secret: string;
  /** `otpauth://totp/...`。手入力や別端末への引き継ぎ用。 */
  uri: string;
  /**
   * QR コードの **PNG の data URI**（`data:image/png;base64,...`）。
   *
   * **キー名を API に合わせること。** 以前ここは `otpauthUrl` / `qrSvg` で、
   * API（`routes/auth/common.ts` の `{ secret, uri, qrDataUrl }`）と
   * 食い違っていた。存在しないキーを読むので `undefined` になり、
   * **QR が表示されないまま 2 要素認証を登録できない**状態だった
   * （2026-08-30 に AWS で顕在化）。型が合っていても実行時に落ちないので
   * 型検査は素通りする。
   */
  qrDataUrl: string;
}

export async function setupTotp(): Promise<TotpSetup> {
  return request<TotpSetup>(`${AUTH_PREFIX}/totp/setup`, { method: 'POST' });
}

export interface TotpVerifyResult {
  enabled: boolean;
  recoveryCodes: string[];
  /**
   * **2 要素へ上がった新しいアクセストークン。**
   *
   * サーバは登録の確定と同時にセッションを差し替える
   * （`routes/auth/common.ts` の `upgradeSessionToMfa()`）。**受け取ったら
   * 必ず載せ替えること。**載せ替えないと手元の JWT は `amr: ['pwd']` のままで、
   * `opsGuard` が全 ops API に `MFA_REQUIRED` を返し続ける。
   *
   * サーバ側の差し替えに失敗したときだけ欠ける（TOTP は有効・リカバリコードは
   * 表示できる、という状態を優先するため応答自体は返る）。
   */
  accessToken?: string;
  expiresIn?: number;
}

/**
 * TOTP 登録の確定。
 *
 * **新しいトークンを載せ替え、`/me` を引き直す。** どちらかを欠くと、
 * 登録が成功しているのに画面には「2 要素認証が未登録です」の警告が
 * 出たままになり、操作はすべて `MFA_REQUIRED` で拒否され続ける
 * （2026-08-30 に AWS で顕在化。利用者はログアウトしないと抜けられなかった）。
 *
 * **`/me` の失敗でこの関数を失敗させない。** サーバは「TOTP は有効化した／
 * セッションの差し替えには失敗した」という状態でも応答を返す設計で、
 * その応答にしか**平文のリカバリコードは載らない**（再表示できない）。
 * ここで throw すると呼び出し側の `onSuccess` が走らず、
 * **2 要素は有効・リカバリコードは永久に失われた**という最悪の状態になる。
 * 画面へは `accessToken` の有無で「入り直しが要るか」を伝える。
 */
export async function verifyTotpSetup(code: string): Promise<TotpVerifyResult> {
  const res = await request<TotpVerifyResult>(`${AUTH_PREFIX}/totp/verify`, {
    method: 'POST',
    body: { code },
  });
  if (res.accessToken) setAccessToken(res.accessToken);
  // トークンが返らなかった場合でも `/me` は引き直す（`mfaEnabled` を実態に
  // 合わせるのが目的。ガード画面はこの値だけを見て出し分けている）。
  try {
    authStore.setUser(await fetchMe());
  } catch {
    // 差し替えに失敗したセッションでは 401 になりうる。リカバリコードを
    // 返しきることを優先する（画面は再ログインを促す）。
  }
  return res;
}

/* --------------------------------------------------------------- 再認証 */

export interface ReauthResult {
  accessToken: string;
  expiresIn: number;
  /** この時刻までは fresh-auth の窓が開いている（既定 5 分）。 */
  freshUntil: string;
}

/**
 * fresh-auth（直近 5 分の 2 要素目）の取り直し（`POST /api/v1/auth/ops/reauth`）。
 *
 * **呼び出し前のアクセストークンはサーバ側でその場で失効する**（セッションを
 * 差し替える実装）。応答の `accessToken` に必ず入れ替えること。入れ替えないと
 * 以降のリクエストが全部 401 になる。
 *
 * `noReauth` を付けるのは、これ自身が 401 を返したときに再認証ダイアログを
 * 再帰的に開かないため。
 */
export async function reauth(method: 'totp' | 'recovery', code: string): Promise<ReauthResult> {
  const res = await request<ReauthResult>(`${AUTH_PREFIX}/reauth`, {
    method: 'POST',
    body: { method, code },
    noRefresh: true,
    noReauth: true,
    silent: true, // ダイアログ内の Alert で見せる
  });
  setAccessToken(res.accessToken);
  return res;
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await request<void>(`${AUTH_PREFIX}/password/change`, {
    method: 'POST',
    body: { currentPassword, newPassword },
  });
}

/**
 * セッション一覧の 1 行 ＝ **1 回のログイン**（サーバ側で `family_id` に畳んである）。
 *
 * `sessions` の生の行は「1 本の refresh トークン」なので、15 分ごとの
 * ローテーションで際限なく増える。畳む前の行をそのまま並べていた頃は、
 * 運営者 1 人・実利用は数回なのに一覧が 60 行になり、
 * 「見覚えのない端末に気付く」という画面の目的が失われていた。
 */
export interface SessionRow {
  id: string;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  /** API は `lastUsedAt`。未使用のセッションは null なので発行時刻で代用する。 */
  lastSeenAt: string;
  expiresAt: string;
  active: boolean;
  current: boolean;
  /** このログインが何本の refresh トークンに更新されたか（内訳の目安）。 */
  rotations: number;
}

/** `GET /sessions` の生応答（`{items,total}` 封筒）。 */
interface SessionListResponse {
  items: Array<{
    id: string;
    ip: string | null;
    userAgent: string | null;
    createdAt: string;
    lastUsedAt: string | null;
    expiresAt: string;
    active: boolean;
    current: boolean;
    rotations?: number;
  }>;
  total: number;
}

export async function fetchSessions(): Promise<SessionRow[]> {
  const res = await request<SessionListResponse>(`${AUTH_PREFIX}/sessions`, {
    method: 'GET',
    silent: true,
  });
  return res.items.map((s) => ({
    id: s.id,
    ip: s.ip,
    userAgent: s.userAgent,
    createdAt: s.createdAt,
    lastSeenAt: s.lastUsedAt ?? s.createdAt,
    expiresAt: s.expiresAt,
    active: s.active,
    current: s.current,
    rotations: s.rotations ?? 1,
  }));
}

export async function revokeOtherSessions(): Promise<{ revoked: number }> {
  return request<{ revoked: number }>(`${AUTH_PREFIX}/sessions/revoke-others`, { method: 'POST' });
}

/**
 * ops 面の代理ログイン（`POST /api/v1/ops/orgs/:id/impersonate`）。
 *
 * **サーバの契約は `{ reason, scope, durationMinutes, approvedByStaffUserId? }`。**
 * 以前ここは `{ reason, writable, ttlSec }` を送っていた。サーバ側の zod が
 * 非 strict だったため `writable` は黙って捨てられ、`read_write` を選んでも
 * **常に read_only になっていた**（しかもエラーにならないので気付けない）。
 * サーバ側を `.strict()` にしたので、以後この形がずれると 400 で即座に出る。
 */
export async function impersonate(
  organizationId: string,
  reason: string,
  options: {
    /** `read_write` は superadmin かつ別 staff の承認が必須。 */
    scope?: 'read_only' | 'read_write';
    durationMinutes?: number;
    approvedByStaffUserId?: string;
  } = {},
): Promise<ImpersonateResult> {
  return api.post(`/orgs/${organizationId}/impersonate`, {
    reason,
    scope: options.scope ?? 'read_only',
    durationMinutes: options.durationMinutes ?? 30,
    ...(options.approvedByStaffUserId
      ? { approvedByStaffUserId: options.approvedByStaffUserId }
      : {}),
  });
}

/** 代理ログインの終了（`POST /api/v1/ops/orgs/:id/impersonate/end`）。自分のぶんだけ閉じる。 */
export async function endImpersonation(
  organizationId: string,
): Promise<{ ok: boolean; ended: number; revokedSessions: number }> {
  return api.post(`/orgs/${organizationId}/impersonate/end`, {});
}

/**
 * 稼働中の代理セッション一覧（`GET /api/v1/ops/impersonations`）。
 *
 * 赤帯をクライアント状態だけで描いていると、**リロードした運営者は自分が
 * 代理中であることを見失う**（サーバ側のセッションは 60 分生きたまま）。
 * 一覧はサーバの `impersonation_sessions` が正なので、そこから復元する。
 */
export async function fetchImpersonations(
  options: { all?: boolean; organizationId?: string } = {},
): Promise<ImpersonationSession[]> {
  const res = await api.get<{ items: ImpersonationSession[] }>('/impersonations', {
    all: options.all ? '1' : undefined,
    organizationId: options.organizationId,
  });
  return res.items;
}

/** 他の運営者のぶんも含めて ID 指定で終了させる（`POST /impersonations/:id/end`）。 */
export async function endImpersonationById(
  impersonationId: string,
): Promise<{ ok: boolean; ended: number; revokedSessions: number }> {
  return api.post(`/impersonations/${impersonationId}/end`, {});
}
